import { NoteCommunicationService } from './../note-communication.service';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { DialogService } from '../../../services/core/dialog.service';
import { ToasterService } from '../../../services/core/toaster.service';
import { StaffService } from 'src/app/services/staff/staff.service';
import { TaskDataService } from '../../sale/task/task.data.service';
import { NoteObserverParticipantPopupComponent } from '../note-observer-participant-popup/note-observer-participant-popup.component';
import { NoteDataService } from '../note.data.service';
import { AppConstants } from 'src/app/constants/app.constants';
import { NoteSidebarComponent } from './note-sidebar/note-sidebar.component';
import { NoteHeaderComponent } from './note-header/note-header.component';
import { NoteMainContentComponent } from './note-main-content/note-main-content.component';
import { ConfirmationPopupComponent } from '../../shared/confirmation-popup/confirmation-popup.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MODULE_IDS } from 'src/app/constants/module-ids.constant';

@Component({
    selector: 'app-create-note',
    templateUrl: './create-note.component.html',
    styleUrls: ['./create-note.component.scss'],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { showError: true },
        },
    ],
    standalone: false
})
export class CreateNoteComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(NoteSidebarComponent) noteSidebar!: NoteSidebarComponent;
  @ViewChild(NoteHeaderComponent) noteHeader!: NoteHeaderComponent;
  @ViewChild(NoteMainContentComponent)
  noteMainContent!: NoteMainContentComponent;

  // Debounce subject for mention search
  private mentionSearchSubject = new Subject<string>();

  // Subscription for note form changes (enables real-time header updates)
  private noteFormSubscription!: Subscription;

  moduleID!: number;
  referenceID!: number | null;
  noteId!: number;
  isNoteModule: boolean = true;
  modulesList: any = [];
  showSearchInput: boolean = true;
  isEdit: boolean = false;
  mode: 'create' | 'edit' | 'view' = 'create';

  // UI State
  isSidebarCollapsed = false;
  isFullscreen = false;
  isSearchOpen = false;
  activeTabIndex = 0;
  isSaving = false;
  lastNonHighPriority: number = 1;
  hideDetailsCommentsHistory: boolean = false;

  searchParams = {
    type: '',
    module_id: '',
    search: '',
  };

  selectedItem: any = {};
  selectedMentionedUsers: any = [];
  responsibleUsersArray: any = [];
  model: any;

  public NOTE_MODULE_ID: number = 23;
  public CONST_CREATED_BY: boolean = false;
  public CONST_OBSERVER: boolean = false;
  public CONST_PARTICIPANT: boolean = false;
  public CONST_RESPONSIBLE_PERSON_TYPE: string = 'responsible_person';
  public CONST_CREATED_BY_TYPE: string = 'created_by';
  public CONST_OBSERVER_TYPE: string = 'observer';
  public CONST_PARTICIPANT_TYPE: string = 'participant';

  public CONST_FLAG: boolean = false;

  createdByArray: any;
  observerArray: any = [];
  participantArray: any = [];
  stages: any = [];
  selectedStage: any = {};
  mentions: any = [];
  isMentionsLoading: boolean = false;
  attachmentsArr = [];
  attachmentsToAdd = [];
  allowedFileTypes: any = [];

  // Status management (matching task pattern)
  statuses: any[] = [];
  selectedStatus: any = null;

  // Tab counts
  attachmentsCount: number = 0;
  commentsCount: number = 0;
  historyCount: number = 0;

  noteForm: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl(null, [Validators.required]),
    description: new UntypedFormControl(null),
    flag: new UntypedFormControl(false),
    module_id: new UntypedFormControl(null),
    reference_id: new UntypedFormControl(null),
    status: new UntypedFormControl(null),
    priority: new UntypedFormControl(1),
    is_template: new UntypedFormControl(false),
    self_assign: new UntypedFormControl(0),
  });

  constructor(
    public dialogService: DialogService,
    public dialog: NgDialogAnimationService,
    private cd: ChangeDetectorRef,
    public toasterService: ToasterService,
    public noteDataService: NoteDataService,
    public taskDataService: TaskDataService,
    private staffService: StaffService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CreateNoteComponent>,
    public backendApiService: BackendApiService,
    private noteCommunicationService: NoteCommunicationService
  ) {
    if (Object.keys(data).length > 0) {
      this.moduleID = this.data?.moduleID;
      this.referenceID = this.data?.referenceID;
      this.isNoteModule = this.data?.isNoteModule;
      this.isEdit = this.data?.isEdit;
      this.model = this.data?.model;
      this.noteId = this.data?.model?.id;
    }
    this.allowedFileTypes = AppConstants.documentFileTypes;

    // Set mode and hideDetailsCommentsHistory based on isEdit BEFORE first render
    // This ensures tabs render correctly from the start
    if (this.isEdit) {
      this.mode = 'edit';
      this.hideDetailsCommentsHistory = false; // Show all 4 tabs
    } else {
      this.mode = 'create';
      this.hideDetailsCommentsHistory = true; // Hide Details/Comments/History, only show Attachments
    }
  }

  ngOnInit(): void {
    this.noteForm.get('module_id')?.setValue(this.moduleID);
    this.noteForm.get('reference_id')?.setValue(this.referenceID);
    if (!this.isEdit && this.data?.noteTitle) {
      this.noteForm.get('name')?.setValue(this.data.noteTitle);
    }
    if (!this.isEdit && this.data?.notePriority !== undefined && this.data?.notePriority !== null) {
      this.noteForm.get('priority')?.setValue(this.data.notePriority);
    }
    this.getModules();
    this.getStatuses();
    if (this.moduleID) {
      this.getStagesOfModule(this.moduleID);
    }

    // Load initial mentions and setup search
    this.loadInitialMentions();
    this.setupMentionSearch();

    // Subscribe to note form changes for real-time header updates
    this.subscribeToNoteFormChanges();
  }

  loadInitialMentions(): void {
    this.isMentionsLoading = true;
    this.backendApiService
      .getApi('staff?search=', [], false)
      .subscribe({
        next: (response: any) => {
          this.mentions = this.staffService.processStaff(response.data || []);
          this.isMentionsLoading = false;
          console.log('[MENTIONS] Initial load:', this.mentions.length, 'users');
        },
        error: (err: any) => {
          console.error('[MENTIONS] Failed to load initial users:', err);
          this.mentions = [];
          this.isMentionsLoading = false;
        },
      });
  }

  setupMentionSearch(): void {
    this.mentionSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.performMentionSearch(searchTerm);
      });
  }

  performMentionSearch(searchTerm: string): void {
    this.isMentionsLoading = true;
    this.backendApiService
      .getApi(`staff?search=${searchTerm}`, [], false)
      .subscribe({
        next: (response: any) => {
          this.mentions = this.staffService.processStaff(response.data || []);
          this.isMentionsLoading = false;
          console.log('[MENTIONS] Search results for "' + searchTerm + '":', this.mentions.length, 'users');
        },
        error: (err: any) => {
          console.error('[MENTIONS] Search failed:', err);
          this.mentions = [];
          this.isMentionsLoading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.mentionSearchSubject.complete();
    if (this.noteFormSubscription) {
      this.noteFormSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    // Mode and hideDetailsCommentsHistory are now set in constructor
    // Just ensure activeTabIndex is set and load data if editing
    this.activeTabIndex = 0;

    if (this.isEdit) {
      this.setDataOnEdit();
    }
  }

  // ========================================================================
  // Real-Time Form Update Subscription
  // ========================================================================

  /**
   * Subscribe to note form changes to enable real-time header updates
   * Follows task module pattern with valueChanges observable
   */
  private subscribeToNoteFormChanges(): void {
    this.noteFormSubscription = this.noteForm
      .get('name')
      ?.valueChanges.subscribe((value) => {
        // Force change detection for OnPush strategy
        // The header binding [noteName]="noteForm.get('name')?.value"
        // will update automatically when change detection runs
        this.cd.markForCheck();
      }) as Subscription;
  }

  // ========================================================================
  // Child Component Event Handlers
  // ========================================================================

  /**
   * Handle field changes from child components
   */
  handleFieldChange(event: { field: string; value: any }): void {
    this.noteForm.get(event.field)?.setValue(event.value);

    if (event.field === 'flag') {
      this.CONST_FLAG = event.value;
    }
  }

  /**
   * Handle module selection from sidebar
   */
  handleModuleSelected(module: any): void {
    this.moduleID = module.id;
    this.noteForm.get('module_id')?.setValue(module.id);
    // Clear reference when module changes
    this.noteForm.get('reference_id')?.setValue(null);
    this.referenceID = null;
  }

  /**
   * Handle reference selection from sidebar
   */
  handleReferenceSelected(reference: any): void {
    this.referenceID = reference.id;
    this.noteForm.get('reference_id')?.setValue(reference.id);
  }

  /**
   * Handle self-assign change from sidebar
   */
  handleSelfAssignChange(isOn: boolean): void {
    // Self-assign logic is handled in the sidebar component
    // This handler is here for future extensions if needed
  }

  /**
   * Handle sidebar collapse toggle
   */
  handleSidebarCollapse(isCollapsed: boolean): void {
    this.isSidebarCollapsed = isCollapsed;
  }

  /**
   * Handle flag toggle from header
   */
  handleFlagToggle(flagged: boolean): void {
    this.CONST_FLAG = flagged;
    this.noteForm.get('flag')?.setValue(flagged);
  }

  /**
   * Handle search toggle from header
   */
  handleSearchToggle(): void {
    this.isSearchOpen = !this.isSearchOpen;
    // Implement search functionality here if needed
  }

  /**
   * Handle fullscreen toggle from header
   */
  handleFullscreenToggle(): void {
    this.isFullscreen = !this.isFullscreen;

    // Update the dialog ref if it has fullscreen methods
    if (this.dialogRef && this.dialogRef.updateSize) {
      if (this.isFullscreen) {
        this.dialogRef.updateSize('100vw', '100vh');
        this.dialogRef.addPanelClass('fullscreen-dialog');
      } else {
        this.dialogRef.updateSize('90vw', '90vh');
        this.dialogRef.removePanelClass('fullscreen-dialog');
      }
    }
  }

  /**
   * Handle delete note from header
   */
  handleDeleteNote(): void {
    if (!this.noteId) return;

    // Open confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationPopupComponent, {
      width: '500px',
      height: '300px',
      data: {},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteNote();
      }
    });
  }

  /**
   * Delete note after confirmation
   */
  private deleteNote(): void {
    this.backendApiService
      .deleteApi(`notes/${this.noteId}`)
      .subscribe((response: any) => {
        this.toasterService.showSuccessMessage(response?.data?.message || 'Note deleted successfully');
        this.noteCommunicationService.setNoteUpdate({ action: 'deleted', noteId: this.noteId });
        this.close(true);
      });
  }

  /**
   * Handle tab change from main content
   */
  handleTabChange(index: number): void {
    this.activeTabIndex = index;
  }

  /**
   * Handle priority toggle from header
   */
  handlePriorityToggle(): void {
    const currentPriority = this.noteForm.get('priority')?.value || 0;
    const isHighPriority = currentPriority === 3 || currentPriority === 4;

    if (isHighPriority) {
      // Toggle off - restore last non-high priority
      this.noteForm.get('priority')?.setValue(this.lastNonHighPriority);
    } else {
      // Toggle on - set to high
      this.lastNonHighPriority = currentPriority;
      this.noteForm.get('priority')?.setValue(3);
    }
  }

  /**
   * Handle mark complete from header
   */
  handleMarkComplete(): void {
    this.toasterService.showInfo('Mark complete feature coming soon!');
  }

  /**
   * Handle PDF export from header
   */
  handleExportPDF(): void {
    this.toasterService.showInfo('PDF export feature coming soon!');
  }

  /**
   * Handle edit note from header
   */
  handleEditNote(): void {
    this.mode = 'edit';
    this.hideDetailsCommentsHistory = false;
    this.activeTabIndex = 0;
  }

  /**
   * Handle new note from header
   */
  handleNewNote(): void {
    this.dialogRef.close();
    // Parent should handle opening a new create-note dialog
  }

  /**
   * Handle edit description from main content
   */
  handleEditDescription(): void {
    // Switch to edit mode if in view mode
    if (this.mode === 'view') {
      this.mode = 'edit';
    }
  }

  /**
   * Handle status change from sidebar
   * @param status Full status object from sidebar
   */
  handleStatusChange(status: any): void {
    if (status?.id) {
      this.noteForm.get('status')?.setValue(status.id);
      this.selectedStatus = status;
    }
  }

  // ========================================================================
  // Helper Methods
  // ========================================================================

  /**
   * Get module label for display
   */
  getModuleLabel(): string {
    if (!this.moduleID || !this.modulesList.length) return '';
    const module = this.modulesList.find((m: any) => m.id === this.moduleID);
    return module?.name || '';
  }

  /**
   * Get the linked record's display name. Prefers the interactively
   * search-selected item (create mode, before the model reloads); falls
   * back to model.reference.name so reopening an existing note in edit
   * mode — where selectedItem was never populated — still shows it.
   */
  getProjectName(): string {
    return this.selectedItem?.data || this.model?.reference?.name || '';
  }

  /**
   * Linked record's id, shown as "#id" next to the module/reference name.
   */
  getRecordNumber(): string {
    return this.referenceID?.toString() || '';
  }

  /**
   * Detail-page routes for module ids a note's linked record can point to.
   * Mirrors view-note-detail.component.ts's LINKED_RECORD_ROUTES.
   */
  private readonly LINKED_RECORD_ROUTES: Partial<Record<number, (id: number | string) => string>> = {
    [MODULE_IDS.PRODUCT]: (id) => `/products/product/${id}`,
    [MODULE_IDS.STAFF]: (id) => `/hr/staff/${id}`,
    [MODULE_IDS.VENDOR]: (id) => `/vendors/vendor/${id}`,
    [MODULE_IDS.CUSTOMER]: (id) => `/customers/customer/${id}`,
    [MODULE_IDS.BID]: (id) => `/bids/bid/${id}`,
    [MODULE_IDS.WAREHOUSE]: (id) => `/warehouses/warehouse/${id}`,
    [MODULE_IDS.JOB]: (id) => `/jobs/detail/${id}`,
    [MODULE_IDS.CHANGE_ORDER]: (id) => `/change-order/edit/${id}`,
  };

  /**
   * Route to the note's linked module record in edit/create mode, using
   * moduleID/referenceID (already tracked across both modes), or null when
   * the module isn't mapped or no record is selected yet.
   */
  getRecordRoute(): string | null {
    if (!this.moduleID || !this.referenceID) {
      return null;
    }

    const buildRoute = this.LINKED_RECORD_ROUTES[this.moduleID];
    return buildRoute ? buildRoute(this.referenceID) : null;
  }

  setDataOnEdit() {
    this.selectedMentionedUsers = this.model.mentions.map((mention: any) => {
      return {
        user: mention,
        type: 'Users',
      };
    });
    const filesData = this.model.relations.attach_files.map((file: any) => {
      return {
        attach_file: file,
        attachment_id: file.id,
      };
    });
    if (this.model?.priority == 1) {
      this.changeFlag();
    }
    this.attachmentsArr = filesData;
    this.attachmentsCount = filesData.length;
    this.noteForm.patchValue({
      name: this.model?.name,
      description: this.model?.description,
      flag: this.model?.flag == 1 ? true : false,
      code: this.model?.code,
      module_id: this.moduleID,
      reference_id: this.referenceID,
      status: this.model.status,
      priority: this.model?.priority || 0,
    });

    this.CONST_FLAG = this.model?.flag == 1 ? true : false;
    this.createdByArray = this.model?.created_by;
    this.selectedStatus = this.model?.status_label || null;

    // CRITICAL FIX: Render existing mentions as chips in the description
    // Extract user objects from selectedMentionedUsers for setDescriptionContent
    const mentionedUsers = this.selectedMentionedUsers.map((item: any) => item.user || item);
    
    // Use setTimeout to ensure ViewChild is available after ngAfterViewInit
    setTimeout(() => {
      if (this.noteMainContent && this.model?.description) {
        // setDescriptionContent will automatically extract and emit mentions
        // This ensures existing mentions are captured in selectedMentionedUsers
        this.noteMainContent.setDescriptionContent(
          this.model.description,
          mentionedUsers
        );
      }
    }, 100);
  }

  save() {
    const form = this.noteForm.value;
    let isInvalid = false;

    if (!form.name) {
      isInvalid = true;
      this.toasterService.showWarning('Name', 'Name field is required.');
    }

    if (!form.reference_id) {
      isInvalid = true;
      this.toasterService.showWarning(
        'Reference',
        'Reference Number field is required.'
      );
    }

    if (!form.module_id) {
      isInvalid = true;
      this.toasterService.showWarning('Module', 'Module Field is Required.');
    }

    if (isInvalid) {
      return true;
    }

    // Prevent multiple saves
    if (this.isSaving) {
      return true;
    }

    this.isSaving = true;

    const service = this.noteId
      ? this.backendApiService.patchApi(
          `notes/${this.model?.id}`,
          this.getFormData()
        )
      : this.backendApiService.postApi('notes/create', this.getFormData());

    service.subscribe(
      (resposne: any) => {
        this.isSaving = false;
        this.toasterService.showSuccessMessage(resposne.data?.message);
        this.close(true);
      },
      (error: any) => {
        this.isSaving = false;
        this.toasterService.showError(
          error?.error?.message || 'Failed to save note. Please try again.'
        );
      }
    );

    return true;
  }

  close = (data: any = null) => {
    this.noteCommunicationService.setNoteUpdate(true);
    this.dialogRef.close(data);
  };

  changeFlag(event: any = '') {
    this.CONST_FLAG = !this.CONST_FLAG;
  }

  /**
   * Handle attachment upload in CREATE/EDIT mode
   * Collects attachment IDs from uploaded files for direct upload (create mode)
   * or folder-based upload (edit mode with note ID)
   */
  getFileID(event: any) {
    console.log('event file id', event);
    const data = event?.attachment;
    if (Array.isArray(data)) {
      this.attachmentsArr = data as any;
      // Extract attachment_ids from each file
      this.attachmentsToAdd = this.attachmentsArr.map(
        (file: any) => file.attachment_id || file.attach_file?.id
      ) as any;
      this.attachmentsCount = this.attachmentsArr.length;
      console.log('Updated attachment IDs:', this.attachmentsToAdd);
    }
  }

  /**
   * Handle attachment deletion in CREATE mode
   * The app-attachment component handles server-side deletion
   * and emits callback with remaining files automatically
   */
  handleAttachmentDelete(event: any): void {
    console.log('Attachment deleted, attachment_ids will be updated on next callback');
  }

  remove(index: any, dataArray: any) {
    dataArray.splice(index, 1);
  }

  hideShowCreatedBy() {
    this.CONST_CREATED_BY = !this.CONST_CREATED_BY;
  }

  hideShowObserver() {
    this.CONST_OBSERVER = !this.CONST_OBSERVER;
  }

  hideShowParticipant() {
    this.CONST_PARTICIPANT = !this.CONST_PARTICIPANT;
  }

  openUsersPopup(event: any, type: any) {
    const position = event.target.getBoundingClientRect();
    const dialogRef = this.dialog.open(NoteObserverParticipantPopupComponent, {
      width: '400px',
      height: '400px',
      position: { top: `${position.top}px`, left: `${position.left}px` },
      data: {
        type: type,
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      console.log('result', result);
      if (result) {
        if (result.type == this.CONST_RESPONSIBLE_PERSON_TYPE) {
          this.responsibleUsersArray.push(result.user);
        } else if (result.type == this.CONST_CREATED_BY_TYPE) {
          this.createdByArray = result.user;
        } else if (result.type == this.CONST_OBSERVER_TYPE) {
          this.observerArray.push(result.user);
        } else if (result.type == this.CONST_PARTICIPANT_TYPE) {
          this.participantArray.push(result.user);
        }
      }
    });
  }

  getModules() {
    this.backendApiService
      .getApi('modules/type?type=note')
      .subscribe((response: any) => {
        this.modulesList = response?.data;
      });
  }

  getStagesOfModule(id: any) {
    this.backendApiService
      .getApi('stages?module_id=' + id)
      .subscribe((response: any) => {
        this.stages = response.data;
      });
  }

  /**
   * Fetch note statuses from API
   * Matches task sidebar pattern exactly
   */
  getStatuses(): void {
    this.backendApiService.getApi('statuses/note').subscribe({
      next: (response: any) => {
        this.statuses = response?.data || [];

        // Set default status if creating new note (not editing)
        if (!this.isEdit && this.statuses.length > 0) {
          const defaultStatus = this.statuses.find(
            (s: any) => s.default_status === true || s.default_status === 1
          );
          if (defaultStatus) {
            this.noteForm.get('status')?.setValue(defaultStatus.id);
          } else if (this.statuses.length > 0) {
            // Fallback to first status if no default is set
            this.noteForm.get('status')?.setValue(this.statuses[0].id);
          }
        }
      },
      error: (err: any) => {
        console.error('Failed to load note statuses:', err);
        this.statuses = [];
      },
    });
  }

  selectedModule(event: any) {
    if (event) {
      this.searchParams.type = event?.slug;
      this.searchParams.module_id = event?.id;
      this.searchParams.search = '';
      this.selectedStage = {};
      this.noteForm.get('module_id')?.setValue(event?.id);
      this.getStagesOfModule(event?.id);
      this.showSearchInput = true;
    } else {
      this.noteForm.get('reference_id')?.setValue('');
      this.noteForm.get('module_id')?.setValue('');
    }
    this.showSearchInput = true;
    this.selectedItem = '';
  }

  onItemSelect(event: any) {
    this.selectedItem = event;
    this.noteForm.get('reference_id')?.setValue(event?.id);
    this.showSearchInput = false;
  }

  updateSearchInputFlag() {
    this.showSearchInput = true;
  }

  getUsers(event: any) {
    const search = typeof event === 'string' ? event : event?.text || '';
    // Use debounced subject instead of direct API call
    this.mentionSearchSubject.next(search);
  }

  getSelectedUser(event: any) {
    // Handle both single user and array (from custom component)
    if (Array.isArray(event)) {
      // Array of mentioned users from child component
      // IMPORTANT: Merge with existing mentions instead of replacing
      // This ensures existing mentions are preserved when adding new ones
      const existingUserIds = new Set(
        this.selectedMentionedUsers.map((x: any) => x.user?.id || x.id)
      );
      
      // Map new mentions to expected format
      const newMentions = event.map((user: any) => ({
        user: user,
        type: 'Users'
      }));
      
      // Add only new mentions that don't already exist
      newMentions.forEach((mention: any) => {
        const userId = mention.user?.id || mention.id;
        if (userId && !existingUserIds.has(userId)) {
          this.selectedMentionedUsers.push(mention);
          existingUserIds.add(userId);
        }
      });
      
      // Also ensure all mentions from event are included (in case format differs)
      // This handles the case where extractAndEmitMentionedUsers sends all mentions
      const eventUserIds = new Set(event.map((u: any) => u.id));
      const currentUserIds = new Set(
        this.selectedMentionedUsers.map((x: any) => x.user?.id || x.id)
      );
      
      // If event contains users not in selectedMentionedUsers, add them
      event.forEach((user: any) => {
        if (user.id && !currentUserIds.has(user.id)) {
          this.selectedMentionedUsers.push({
            user: user,
            type: 'Users'
          });
        }
      });
    } else {
      // Single user selection
      const index = this.selectedMentionedUsers.findIndex(
        (x: any) => x.id == event?.id || x.user?.id == event?.id
      );
      if (index == -1) {
        this.selectedMentionedUsers.push({ user: event, type: 'Users' });
      }
    }
  }

  getFormData() {
    const form = this.noteForm.value;
    return {
      module_id: form.module_id,
      reference_id: form.reference_id,
      name: form.name,
      description: form.description,
      flag: form.flag == true ? 1 : 0,
      mentions: this.selectedMentionedUsers?.map((x: any) => ({
        id: x?.user?.id || x?.id,
        type: 'Users',
      })),
      attachment_ids: this.attachmentsToAdd,
      status: form.status,
      priority: form.priority,
      is_template: form.is_template ? 1 : 0,
      self_assign: form?.self_assign ? 1 : 0,
    };
  }

  // =============================================================================
  // TAB COUNT HANDLERS
  // =============================================================================

  handleAttachmentCountChange(count: number): void {
    this.attachmentsCount = count || 0;
  }

  handleCommentCountChange(count: number): void {
    this.commentsCount = count || 0;
  }

  handleHistoryCountChange(history: number | any[]): void {
    if (Array.isArray(history)) {
      this.historyCount = history.length;
      return;
    }
    this.historyCount = history || 0;
  }
}
